import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUpRight, ExternalLink, Globe, BarChart3, Star,
  Facebook, Twitter, Linkedin, Instagram, Youtube, Link as LinkIcon,
} from 'lucide-react';
import { companiesAPI } from '../lib/api';
import { normalizeRichText } from '../lib/richText';
import { PageHero } from './ModernPage';
import EarningsCountdown from './EarningsCountdown';
import { useSettings } from '../App';
import { opportunitySkin } from '../lib/opportunitySkin';

// ─────────────────────────────────────────────────────────────────────────
// ModernCompanyDetail — the «Moderno» /companies/:slug (s140). Same data and
// actions as the default page (description / news tabs, socials, related KMS
// insights, follow, SEC / company / investor links, fact card), on the
// internal-page standard: PageHero with the company photo, ticker eyebrow and
// logo badge; article (7) + sticky side column (5); Moderno buttons and prose.
// ─────────────────────────────────────────────────────────────────────────

const HEAD = "var(--op-head, 'Playfair Display', Georgia, serif)";
const DARK = 'var(--op-dark, #16171a)';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const line = 'rgba(20,26,58,.12)';
const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, linkedin: Linkedin, instagram: Instagram, youtube: Youtube };

const CSS = `
.mcd-prose{font-size:1.0625rem;line-height:1.78;color:var(--color-body-text,#475569);overflow-wrap:break-word}
.mcd-prose p{margin:0 0 1.1em}
.mcd-prose strong,.mcd-prose b{color:var(--color-heading,#1a2332)}
.mcd-prose a{color:var(--op-strong,#b86500);text-decoration:underline;text-underline-offset:.3em}
.mcd-prose h2,.mcd-prose h3,.mcd-prose h4{font-family:var(--op-head);font-weight:600;color:var(--color-heading,#1a2332);font-size:1.3rem;margin:1.5em 0 .5em}
.mcd-prose ul{list-style:disc;padding-left:1.2em}.mcd-prose ol{list-style:decimal;padding-left:1.2em}
.mcd-social{transition:background-color 180ms ease,border-color 180ms ease,color 180ms ease}
.mcd-news-title{transition:color 180ms ease}
@media (hover:hover) and (pointer:fine){
  .mcd-social:hover{background:var(--color-accent,#ff9c1b);border-color:var(--color-accent,#ff9c1b);color:var(--accent-ink,#0f172a)}
  .mcd-news:hover .mcd-news-title{color:var(--op-strong,#b86500)}
}
.mcd-social:focus-visible{outline:2px solid var(--color-heading,#1a2332);outline-offset:2px}
`;

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? iso : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
function fmtNewsDate(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).replace(' ', 'T'));
  return isNaN(d) ? iso : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const Label = ({ children }) => <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.14em', color: 'rgba(20,26,58,.5)' }}>{children}</span>;

function Fact({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5 py-4 border-b last:border-b-0" style={{ borderColor: line }}>
      <Label>{label}</Label>
      <div className="text-[15px] font-semibold break-words" style={{ color: 'var(--color-heading, #1a2332)' }}>{children || '—'}</div>
    </div>
  );
}

function ActionLink({ href, children, primary, testId }) {
  const cls = `mx-btn ${primary ? 'mx-btn--primary mx-dark' : 'mx-btn--secondary'} w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-[2px] font-semibold text-sm border`;
  const style = primary ? { background: DARK, color: '#fff', borderColor: DARK } : { color: 'var(--color-heading, #1a2332)', borderColor: 'rgba(20,26,58,.25)' };
  return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={testId}>{children}</a>;
}

function Loading() {
  return (
    <div data-testid="company-loading">
      <div className="min-h-[320px] lg:min-h-[400px] animate-pulse" style={{ background: DARK }} />
      <div className={`${shell} py-14 grid gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]`}>
        <div className="h-64 rounded-[var(--op-radius,3px)] animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
        <div className="h-64 rounded-[var(--op-radius,3px)] animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
      </div>
    </div>
  );
}

export default function ModernCompanyDetail() {
  const skin = opportunitySkin(useSettings().active_theme); // s153: per-theme skin, all brands
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [status, setStatus] = useState('loading');
  const [tab, setTab] = useState('description');
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus('loading'); setTab('description');
    companiesAPI.detail(slug).then(r => { setC(r.data); setFollowing(!!r.data.following); setStatus('ok'); }).catch(() => setStatus('notfound'));
  }, [slug]);

  const toggleFollow = async () => {
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await companiesAPI.follow(c.id);
      setFollowing(data.following);
      toast.success(data.following ? 'Added to your Portfolio' : 'Removed from your Portfolio');
    } catch { toast.error('Could not update follow'); }
    finally { setBusy(false); }
  };

  if (status === 'loading') return <Loading />;
  if (status === 'notfound') {
    return (
      <div className="bg-white" style={skin} data-testid="company-notfound">
        <PageHero title="Company not found" summary="The company you are looking for does not exist or is no longer available."
          trail={[{ label: 'Home', to: '/' }, { label: 'Companies', to: '/companies' }, { label: 'Not found' }]} />
        <div className={`${shell} py-14`}>
          <Link to="/companies" className="mx-link inline-flex items-center gap-2 font-bold text-sm" style={{ color: 'var(--op-strong, #b86500)' }} data-testid="company-notfound-back"><ArrowLeft className="w-4 h-4" /> Back to Companies</Link>
        </div>
      </div>
    );
  }

  const socials = Object.entries(c.socials || {}).filter(([, url]) => url);
  const news = c.news || [];
  const indexes = c.indexes || [];
  const posts = c.related_posts || [];
  const badge = c.logo_on ? (
    <span className="grid place-items-center w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white p-2.5 mb-1" style={{ boxShadow: '0 12px 28px -12px rgba(0,0,0,.55)' }}>
      <img src={c.logo_on} alt={`${c.name} logo`} className="w-full h-full object-contain" data-testid="company-logo-overlay" />
    </span>
  ) : null;

  return (
    <div data-testid="company-detail" className="bg-white" style={skin}>
      <style>{CSS}</style>
      <PageHero title={c.name} banner={c.image_detail} badge={badge}
        eyebrow={[c.symbol, c.sector].filter(Boolean).join(' · ')}
        trail={[{ label: 'Home', to: '/' }, { label: 'Companies', to: '/companies' }, { label: c.name }]} />

      <div className={`${shell} py-14 lg:py-20 grid gap-12 lg:gap-16 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]`}>
        {/* ── Main column ── */}
        <div className="min-w-0 flex flex-col gap-14">
          <div>
            <div role="tablist" className="flex gap-7 border-b" style={{ borderColor: line }} data-testid="company-tabs">
              {[['description', 'Description'], ['news', `News${news.length ? ` (${news.length})` : ''}`]].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`company-tab-${k}`}
                  className="-mb-px pb-3 text-sm font-semibold transition-colors duration-150"
                  style={{ color: tab === k ? 'var(--color-heading, #1a2332)' : 'rgba(20,26,58,.5)', borderBottom: `2px solid ${tab === k ? 'var(--color-accent, #ff9c1b)' : 'transparent'}` }}>{label}</button>
              ))}
            </div>

            {tab === 'description' ? (
              <div className="pt-8 flex flex-col gap-8" data-testid="company-description">
                {c.description
                  ? <div className="mcd-prose max-w-[72ch]" dangerouslySetInnerHTML={{ __html: normalizeRichText(c.description) }} />
                  : <p className="italic" style={{ color: 'var(--color-body-text, #475569)' }}>No description available.</p>}
                {socials.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 pt-6 border-t" style={{ borderColor: line }} data-testid="company-socials">
                    <Label>Follow {c.name}</Label>
                    {socials.map(([k, url]) => {
                      const Icon = SOCIAL_ICONS[k] || LinkIcon;
                      return <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={k} className="mcd-social w-9 h-9 grid place-items-center rounded-full border" style={{ borderColor: 'rgba(20,26,58,.2)', color: 'var(--color-heading, #1a2332)' }}><Icon className="w-4 h-4" /></a>;
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="pt-2 flex flex-col" data-testid="company-news">
                {news.length === 0 && <p className="pt-6 italic" style={{ color: 'var(--color-body-text, #475569)' }}>No news available for this company.</p>}
                {news.map((n, i) => (
                  <a key={i} href={n.url || undefined} target="_blank" rel="noopener noreferrer" className="mcd-news group grid gap-5 py-6 border-b sm:grid-cols-[168px_minmax(0,1fr)]" style={{ borderColor: line }}>
                    {n.image ? <span className="hidden sm:block h-24 overflow-hidden rounded-[var(--op-radius,3px)]" style={{ background: DARK }}><img src={n.image} alt="" loading="lazy" className="w-full h-full object-cover" /></span> : <span className="hidden sm:block" />}
                    <span className="flex flex-col gap-2 min-w-0">
                      <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--op-strong, #b86500)' }}>{[fmtNewsDate(n.published_date), n.site].filter(Boolean).join(' · ')}</span>
                      <span className="mcd-news-title font-semibold" style={{ fontFamily: HEAD, fontSize: '1.15rem', lineHeight: 1.3, color: 'var(--color-heading, #1a2332)' }}>{n.title}</span>
                      {n.description && <span className="text-[14px] leading-relaxed line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(n.description) }} />}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {posts.length > 0 && (
            <section className="flex flex-col gap-6 pt-12 border-t" style={{ borderColor: line }} data-testid="company-related-posts">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.16em', color: 'var(--op-strong, #b86500)' }}>Knowledge base</span>
                <h2 className="font-bold" style={{ fontFamily: HEAD, fontSize: 'clamp(1.6rem, 2.6vw, 2.1rem)', color: 'var(--color-heading, #1a2332)' }}>Related <em style={{ fontWeight: 500, color: 'var(--op-strong, #b86500)' }}>insights</em></h2>
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                {posts.map(p => (
                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="group flex flex-col gap-3" data-testid={`company-related-post-${p.id}`}>
                    <span className="block aspect-[16/10] overflow-hidden rounded-[var(--op-radius,3px)]" style={{ background: DARK }}>
                      {p.image && <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
                    </span>
                    <span className="font-semibold inline-flex items-start justify-between gap-3 transition-colors duration-200 group-hover:text-[color:var(--op-strong)]" style={{ fontFamily: HEAD, fontSize: '1.2rem', lineHeight: 1.3, color: 'var(--color-heading, #1a2332)' }}>
                      {p.title}<ArrowUpRight className="w-4 h-4 flex-none mt-1" style={{ color: 'var(--op-strong, #b86500)' }} />
                    </span>
                    {p.summary && <span className="text-[15px] leading-relaxed line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }}>{p.summary}</span>}
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* ── Side column ── */}
        <aside className="flex flex-col gap-6 self-start lg:sticky lg:top-28">
          <EarningsCountdown radius="var(--op-radius, 3px)" date={c.earnings_date} time={c.earnings_time} line={line}
            heading="var(--color-heading, #1a2332)" ink="var(--color-heading, #1a2332)"
            muted="rgba(20,26,58,.55)" accent="var(--accent-strong, #b86500)" font={HEAD} />

          <div className="flex flex-col gap-3">
            <button type="button" onClick={toggleFollow} disabled={busy} data-testid="company-follow-btn"
              className={`mx-btn ${following ? 'mx-btn--secondary' : 'mx-btn--primary'} w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-[2px] font-semibold text-sm border disabled:opacity-60`}
              style={following ? { color: 'var(--color-heading, #1a2332)', borderColor: 'var(--color-heading, #1a2332)' } : { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)', borderColor: 'var(--color-accent, #ff9c1b)' }}>
              <Star className="w-4 h-4" fill={following ? 'currentColor' : 'none'} /> {following ? 'Following' : 'Follow'}
            </button>
            {c.link_sec && <ActionLink href={c.link_sec} primary testId="company-sec-link"><ExternalLink className="w-4 h-4" /> SEC.gov filings</ActionLink>}
            {c.link_company && <ActionLink href={c.link_company} testId="company-site-link"><Globe className="w-4 h-4" /> Company website</ActionLink>}
            {c.investor_url && <ActionLink href={c.investor_url} testId="company-ir-link"><BarChart3 className="w-4 h-4" /> Investor relations</ActionLink>}
          </div>


          <div className="px-6 py-2 rounded-[var(--op-radius,3px)] border" style={{ borderColor: line }} data-testid="company-facts">
            <Fact label="Sector">{c.sector}</Fact>
            <Fact label="Industry">{c.industry}</Fact>
            <Fact label="Index">
              {indexes.length ? <span className="flex flex-wrap gap-1.5">{indexes.map((ix, i) => <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 12%, #fff)', color: 'var(--op-strong, #b86500)' }}>{ix}</span>)}</span> : null}
            </Fact>
            <Fact label="CEO">{c.ceo}</Fact>
            <Fact label="IPO date">{fmtDate(c.ipo_date)}</Fact>
            <Fact label="SEC EDGAR CIK">{c.edgar_cik}</Fact>
          </div>

          <Link to="/companies" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start" style={{ color: 'var(--op-strong, #b86500)' }} data-testid="company-back-sidebar"><ArrowLeft className="w-4 h-4" /> Back to Companies</Link>
        </aside>
      </div>

    </div>
  );
}
